import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Panel, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import { NoResults, ThemedButton } from "@/App/components";
import { formatDMRConfirmationNumber } from "@/App/utils/directMemberReimbursement";

export interface DocumentTabDocument {
  readonly name: string;
  readonly description: string;
  readonly id: string | number;
  readonly submissionId?: number;
  readonly submissionConfirmationNumber?: string;
  readonly documentSource: "external" | "internal";
  readonly documentType: string;
}

export interface DocumentTabProps {
  readonly isLoading: boolean;
  readonly documents: DocumentTabDocument[];
  readonly onDownload: (document: DocumentTabDocument) => void;
}

export const DocumentTab: FC<DocumentTabProps> = ({ documents, onDownload, isLoading }) => {
  const { t: translate } = useTranslation();
  const getDocumentTitle = (document: DocumentTabDocument) => {
    let suffix = "";

    if (document.submissionConfirmationNumber) {
      suffix = ` (Reimbursement Claim ${formatDMRConfirmationNumber(document.submissionConfirmationNumber)})`;
    }

    return `${document.name}${suffix}`;
  };
  const desktopView = useCallback(
    (document: DocumentTabDocument) => {
      return (
        <Panel key={`panel-${document.id}-${document.name}`}>
          <Shelf>
            <Subheading testId={`document-${document.id}-name`}>{getDocumentTitle(document)}</Subheading>
            <Shelf.Spacer />
            <ThemedButton icon="download" testId={`document-${document.id}-download`} appearance="secondary" onClick={() => onDownload(document)}>
              {translate("DOCUMENT_TAB.download")}
            </ThemedButton>
          </Shelf>
          <Paragraph testId={`document-${document.id}-description`}>{document.description || ""}</Paragraph>
        </Panel>
      );
    },
    [onDownload],
  );

  const mobileView = useCallback((document: DocumentTabDocument) => {
    const truncateDescription = (description: string) => {
      if (description.length >= 100) {
        return `${description.substring(0, 97)}...`;
      }
      return description;
    };

    return (
      <Panel key={`panel-${document.id}-${document.name}`}>
        <Stack>
          <Subheading testId={`document-${document.id}-name`}>{document.name}</Subheading>
          <Paragraph testId={`document-${document.id}-description`}>{truncateDescription(document.description || "")}</Paragraph>
          <ThemedButton icon="download" testId={`document-${document.id}-download`} appearance="secondary" onClick={() => onDownload(document)}>
            {translate("DOCUMENT_TAB.download")}
          </ThemedButton>
        </Stack>
      </Panel>
    );
  }, []);

  const { renderDocumentFunction } = useResponsive({
    renderDocumentFunction: {
      mobile: mobileView,
      desktop: desktopView,
    },
  });

  return (
    <LoadingGroup loading={isLoading}>
      <LoadingGroup.Placeholder fallback={
        <Shelf>
          <Shelf.Spacer />
          <Spinner size="large" />
          <Shelf.Spacer />
        </Shelf>
      }>
        <Stack>
          {!isLoading && (documents || []).length === 0 && <NoResults message={translate("DOCUMENT_TAB.noDocumentsFound")} />}
          {(documents || [])?.map(renderDocumentFunction)}
        </Stack>
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
